import React, { useState, useRef, useEffect } from 'react';
import '../../App.css';
import './data-management.css';
import '../../tools/search-results/search-results.css';
import { useAppSelector, useAppDispatch, useSelectedCanfar } from '../../app/hooks';
import { useTranslation } from 'react-i18next';

// icons
import {
  Blocks,
  Braces,
  Check,
  ClipboardPaste,
  Copy,
  File as FileIcon,
  FileDown,
  FilePen,
  FileUp,
  FolderOpen,
  FolderPlus,
  List,
  PackageOpen,
  RefreshCw,
  Scissors,
  ShelvingUnit,
  X as XIcon,
} from 'lucide-react';

// types
import {
  CanfarServiceType,
  DataItemEnabledCondition,
  DataItemType,
  FileBrowserPage,
  JobType,
  Theme,
} from '../../utils/types';
import { RenameType } from './file-browser-slice';

// functions
import { apiPrefix, ContainerHook, displayFileSize } from '../../utils/functions';
import { brw_handleItemClick, brw_keyDownHandler } from '../../utils/item-browser';

// classes
import { ContextMenuType, ContextMenuItem } from '../menus/context-menu';
import { ContextMenu } from '../menus/context-menu';
import Scrollbox from '../../tools/scrollbox';
import PleaseWait from '../../tools/please-wait';
import MiniButton from '../../tools/mini-button';
import {
  Event,
  EventType,
  EventTarget,
  DataCollection,
  GatewaySettingsType,
  InitiateDMSearchParams,
} from '../../utils/events';
import { toast } from 'react-toastify';

//	--------------------------------------------------------------------------
//
//	R E D U X   S T A T E   M A N A G E M E N T
//
//	--------------------------------------------------------------------------

import { store } from '../../app/store';
import { getSelectedCanfar } from '../../app/canfar-selectors';

// import state definition and functions (redux).
import {
  FileBrowserState,
  addToSelectedDivs,
  removeFromSelectedDivs,
  setFilenameFilter,
  setFileTypeFilter,
  setFocusElement,
  setStoragePath,
  setSelectedItems,
  setSelectedDivs,
  setDataList,
  setLoadingComponent,
  setRenameIndex,
  setRenameType,
  setRenameFilename,
  setClipboard,
  setClipboardPath,
  setInClipboardFolder,
  setContextMenuRefresh,
} from './file-browser-slice';
import {
  addTemporaryItem,
  cancelQuery,
  deleteFile,
  downloadFile,
  filenameFilter,
  moveFiles,
  refreshPage,
  renameFile,
} from './file-browser-actions';
import { GlobalState, rG_setSubmittingJob } from '../../app/global-slice';
import { SettingsState } from '../../app/settings-slice';
import {
  CONTEXT_CONTAINERS,
  CONTEXT_SYSTEMS,
  CONTEXT_MENU_FILE_BROWSER,
  ctx_setContextMenuDefinition,
  ctx_setContextRow,
  ctx_setContextVisible,
} from '../menus/context-menu-slice';
import { handleEvent } from '../../app-actions';
import { setUserDropdownMenuDisplayed } from '../../app-slice';
import {
  ctx_buildContextMenu,
  ctx_calculatePosition,
  ctx_keyDownHandler,
} from '../menus/context-menu-actions';

//  --------------------------------------------------------------------------
//
//  T Y P E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  D E C L A R A T I O N S
//
//  --------------------------------------------------------------------------

declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    // extends React's HTMLAttributes
    directory?: string; // remember to make these attributes optional....
    webkitdirectory?: string;
  }
}

//  --------------------------------------------------------------------------
//
//  P R O P E R T I E S
//
//  --------------------------------------------------------------------------

//  --------------------------------------------------------------------------
//
//  C L A S S   D E F I N I T I O N
//
//  --------------------------------------------------------------------------

export default function DataManagementTable() {
  //
  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  // page currently displayed.
  const sPageDisplayed: FileBrowserPage = useAppSelector(
    (state) => state.fileBrowser.pageParams.pageDisplayed,
  );
  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.fileBrowser.loadingComponent,
  );

  // the data items displayed in the table.
  const sDataList: DataItemType[] = useAppSelector((state) => state.fileBrowser.dataList);

  // filter values.
  const sFilenameFilter: string[] = useAppSelector(
    (state) => state.fileBrowser.pageParams.filenameFilter,
  );
  const sFileTypeFilter: string = useAppSelector(
    (state) => state.fileBrowser.pageParams.fileTypeFilter,
  );
  const sShowHiddenFiles: boolean = useAppSelector(
    (state) => state.fileBrowser.pageParams.showHiddenFiles,
  );

  // table state.
  const sNamespace: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.namespace,
  );
  const sJobID: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.jobID,
  );
  const sDataset: string | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.dataset,
  );
  const sCollection: { id: number; name: string } | undefined = useAppSelector(
    (state) => state.fileBrowser.pageParams.collection,
  );

  // user and project storage paths.
  const sStoragePath: string[] = useAppSelector(
    (state) => state.fileBrowser.pageParams.storagePath,
  );

  // other local variables.
  const sRenameIndex: number = useAppSelector((state) => state.fileBrowser.renameIndex);
  const sRenameType: RenameType = useAppSelector((state) => state.fileBrowser.renameType);
  const sRenameFilename: string = useAppSelector(
    (state) => state.fileBrowser.renameFilename,
  );
  const sClipboard: string[] = useAppSelector((state) => state.fileBrowser.clipboard);
  const sClipboardPath: string[] = useAppSelector(
    (state) => state.fileBrowser.clipboardPath,
  );
  const sInClipboardFolder: boolean = useAppSelector(
    (state) => state.fileBrowser.inClipboardFolder,
  );
  const sOpenFileDialogTrigger: number = useAppSelector(
    (state) => state.fileBrowser.openFileDialogTrigger,
  );
  const sFocusElement: { divID: number } = useAppSelector(
    (state) => state.fileBrowser.focusElement,
  );

  // and hold the selected instance of CANFAR.
  const sG_selectedCanfar: CanfarServiceType | undefined = useSelectedCanfar();
  const sG_project: { id: number; name?: string } | undefined = useAppSelector(
    (state) => state.global.project,
  );
  const sG_preferredUsername: string | undefined = useAppSelector(
    (state) => state.global.preferredUsername,
  );
  const sG_darkMode: boolean = useAppSelector((state) => state.global.darkMode);
  const sG_narrowScreen: boolean = useAppSelector((state) => state.global.narrowScreen);

  // selected items.
  const sSelectedItems: DataItemType[] = useAppSelector(
    (state) => state.fileBrowser.selectedItems,
  );
  const sSelectedDivs: number[] = useAppSelector(
    (state) => state.fileBrowser.selectedDivs,
  );

  const sErrorMessage: string = useAppSelector((state) => state.fileBrowser.errorMessage);

  // store the vos URI when we retrieve files from user storage. we will need it
  // for creating directories.
  const sVosUri: string = useAppSelector((state) => state.fileBrowser.vosUri);

  // gateway settings.
  const sIconSize: string | undefined = useAppSelector(
    (state) => state.settings.iconSize,
  );

  // context-menu definition.
  const sContextMenuDefinition: ContextMenuType[][] = useAppSelector(
    (state) => state.contextMenu.contextMenuDefinition,
  );

  //	--------------------------------------------------------------------------
  //
  //	D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // state variables.
  const [sRenameInProgress, setRenameInProgress] = useState<boolean>(false);

  // use current time to trigger actions when events arrive.
  const [sDataCollectionItemsChanged, setDataCollectionItemsChanged] =
    useState<number>(0);
  const [sDataCollectionsChanged, setDataCollectionsChanged] = useState<number>(0);

  // store a timer that fires when the double-click window times out.
  var doubleClickTimeout = useRef<ReturnType<typeof setTimeout>>();

  // this file dialog is used to upload or download files, and is called when the user
  // clicks the context menu item.
  const fileDialogUpload = useRef<HTMLInputElement | null>(null);
  //const fileDialogDownload = useRef<HTMLInputElement | null>(null);

  //	--------------------------------------------------------------------------
  //
  //	F U N C T I O N S
  //
  //	--------------------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	raise a create-collection popup message.
  //
  //	------------------------------------------------------------

  const [sRaiseStorageMessage, setRaiseStorageMessage] = useState<any>(undefined);

  const setStorageMessageObject = (newFunction: any) => {
    setRaiseStorageMessage(() => newFunction);
  }; // setStorageMessageObject

  //	------------------------------------------------------------
  //
  //	An asynchronous function that adds a folder to
  //	user storage.
  //
  //	------------------------------------------------------------

  async function addFolder(
    args: { name: string; storageType?: 'user' | 'project' } = {
      name: '',
      storageType: 'user',
    },
  ) {
    //
    const stateFetch: any = store.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;
    const stateGlobal: GlobalState = stateFetch.global;

    const { name, storageType = 'user' } = args;

    const path = state.pageParams.storagePath?.join('/') ?? '';
    const url = getSelectedCanfar(stateFetch)?.url ?? '';
    const username = stateGlobal.preferredUsername ?? '';
    const project_name = stateGlobal.project?.name ?? '';

    // Base path for VOS
    let fullPath = '';
    if (storageType === 'user') {
      fullPath = `/home/${username}/${path}${name}`;
    } else {
      fullPath = `/projects/${project_name}/${path}${name}`;
    }

    const urlCommand =
      `${apiPrefix()}/v1/storage/create_directory` +
      `?url=${encodeURIComponent(url)}` +
      `&path=${encodeURIComponent(fullPath)}` +
      `&created_by=${encodeURIComponent(username)}` +
      `&vos_uri=${encodeURIComponent(sVosUri)}`;

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (apiResult.ok) {
        dispatch(refreshPage());
      } else {
        // Status Code we get is 502 with response body
        let rawJson = await apiResult.text();
        const parsedJson = JSON.parse(rawJson, (key, value) => {
          return value;
        });
        console.log('Failed to create directory:', parsedJson.detail);
        if (parsedJson.detail.includes('403 Forbidden')) {
          dispatch(refreshPage());
          toast.error('Request forbidden by administrative rules.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: stateGlobal.darkMode ? 'dark' : 'light',
          });
        }
      }
    } catch (e) {
      console.error('Error during directory creation:', e);
    }
    //
  } // addFolder

  //	------------------------------------------------------------
  //
  //	Set the focus to a specific item in the file browser.
  //
  //	------------------------------------------------------------

  const focusDataItem = () => {
    //
    const stateFetch: any = store.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;

    window.requestAnimationFrame(() => {
      const target = document.getElementById(`itmData_${state.focusElement.divID}`);
      if (target instanceof HTMLElement) {
        target.focus();
      }
    });
    //
  }; // focusDataItem

  //	------------------------------------------------------------
  //
  //	Work out how many items stretch across the row in the
  //	browser.
  //
  //	------------------------------------------------------------

  const getItemsPerRow = () => {
    //
    const stateFetch: any = store.getState();
    const stateSettings: SettingsState = stateFetch.settings;

    if (stateSettings.iconSize === 'list') {
      return 1;
    }
    const container = document.getElementById('file-browser-container');
    const sample =
      container?.querySelector<HTMLDivElement>('.data-management-item') ?? null;
    if (!container || !sample) {
      return 1;
    }
    const width = sample.getBoundingClientRect().width || 1;

    return Math.max(1, Math.floor(container.clientWidth / width));
    //
  }; // getItemsPerRow

  //	------------------------------------------------------------
  //
  //	builds a list of the selected items.
  //
  //	------------------------------------------------------------

  function getSelectedItems(args: { divID: number[] }) {
    //
    const selectedItems: DataItemType[] = args.divID.map((divID: number) => {
      return {
        namespace:
          (sNamespace ?? '') !== '' ? (sNamespace ?? '') : sDataList[divID].namespace,
        filename: sDataList[divID].filename,
        did_type: sDataList[divID].did_type ?? '',
        bytes: sDataList[divID].bytes ?? 0,
      };
    });

    // return something.
    return selectedItems;
    //
  } // getSelectedItems

  //	------------------------------------------------------------
  //
  //	Handler for lost-focus events on the input boxes.
  //
  //	------------------------------------------------------------

  const onBlurEvent = (event: React.FocusEvent<HTMLInputElement>) => {
    const inputBox: HTMLInputElement = event.target;

    if (inputBox.name === 'filename') {
      //
      // update the state variable that holds the current value of the filename.
      let filenames: string[] = sFilenameFilter.slice();
      filenames[filenames.length - 1] = inputBox.value;
      dispatch(setFilenameFilter(filenames));

      // run the data-management search.
      const params: InitiateDMSearchParams = {
        namespace: sNamespace,
        jobID: sJobID,
        dataset: sDataset,
        project: sG_project,
        fileType:
          sPageDisplayed === FileBrowserPage.FileBrowser || FileBrowserPage.Namespaces
            ? 'all'
            : sFileTypeFilter,
        preferredUsername: sG_preferredUsername,
        storagePath: sStoragePath,
        canfarService: sG_selectedCanfar,
        showHiddenFiles: sShowHiddenFiles,
        pageDisplayed: sPageDisplayed,
        storageType: sPageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
        focus: true,
      };
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
        parameters: params,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }
    //
  }; // onBlurEvent

  //	------------------------------------------------------------
  //
  //	Handler for changes to the input boxes.
  //
  //	------------------------------------------------------------

  const onChangeEvent = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    // update the state for any input boxes that have been changed.
    if (inputBox.name === 'filename' && sFilenameFilter.length > 0) {
      let filenames: string[] = sFilenameFilter.slice();
      filenames[filenames.length - 1] = inputBox.value;
      dispatch(setFilenameFilter(filenames));
    }
    //
  }; // onChangeEvent

  //	------------------------------------------------------------
  //
  //	Handler for select box onChange event.
  //
  //	------------------------------------------------------------

  const onChangeSelectHandler = (event: React.ChangeEvent<HTMLSelectElement>) => {
    //
    const selectBox: HTMLSelectElement = event.target;

    // change to the selected file type.
    if (selectBox.id === 'file-type-combo') {
      //
      // update the state variable that holds the current value of the file type listbox.
      dispatch(setFileTypeFilter(selectBox.value));

      // run the data-management search.
      const params: InitiateDMSearchParams = {
        namespace: sNamespace,
        filename: filenameFilter(),
        jobID: sJobID,
        dataset: sDataset,
        project: sG_project,
        fileType: selectBox.value,
        preferredUsername: sG_preferredUsername,
        storagePath: sStoragePath,
        canfarService: sG_selectedCanfar,
        showHiddenFiles: sShowHiddenFiles,
        pageDisplayed: sPageDisplayed,
        storageType: sPageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
      };
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
        parameters: params,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
      //
    }
    //
  }; // onChangeSelectHandler

  //	------------------------------------------------------------
  //
  //	Handler for different clicks logic.
  //
  //	------------------------------------------------------------

  const onClickHandler = (event: React.MouseEvent<HTMLElement>) => {
    //
    const stateFetch: any = store.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;

    const divElement = event.currentTarget;
    const rightClick = event.type === 'contextmenu';
    const doubleClick = event.detail === 2;

    // Handle clicking on empty container
    if (isClickOnEmptyContainer(event)) {
      handleContainerDeselection();
    }

    // Handle selection and navigation on item click
    if (divElement.id.startsWith('itmData_') && divElement instanceof HTMLDivElement) {
      const divID = Number(divElement.id.slice(8));
      if (isNaN(divID) === false) {
        //
        brw_handleItemClick({
          divID: divID,
          shiftKey: event.shiftKey,
          ctrlKey: event.ctrlKey,
          doubleClick: doubleClick,
          selectedDivs: state.selectedDivs,
          setSelectedDivs: setSelectedDivs,
          addToSelectedDivs: addToSelectedDivs,
          removeFromSelectedDivs: removeFromSelectedDivs,
          handleDoubleClick: handleDoubleClickNavigation,
          focusElement: state.focusElement.divID,
          setFocusElement: setFocusElement,
        });
        //
      }
    }

    // Handle right-click context menu
    if (
      rightClick &&
      divElement.id === 'file-browser-container' &&
      divElement instanceof HTMLDivElement
    ) {
      handleRightClickContextMenu(event as React.MouseEvent<HTMLDivElement>, divElement);
      event.preventDefault();
    }

    // Handle toolbar actions (e.g. icon size, upload, rename, etc.)
    handleToolbarActions(event);
    //
  }; // onClickHandler

  //	------------------------------------------------------------
  //
  //	Function to check clicking on the main container
  //
  //	------------------------------------------------------------

  function isClickOnEmptyContainer(event: React.MouseEvent<HTMLElement>): boolean {
    return (
      event.target instanceof HTMLDivElement &&
      event.target.id === 'file-browser-container'
    );
  }

  //  ------------------------------------------------------------
  //
  //  Function that clears all selected items and resets the
  //  context menu state when an empty area of the container is
  //  clicked.
  //
  //  ------------------------------------------------------------

  function handleContainerDeselection() {
    //
    dispatch(setSelectedDivs([]));

    // update enabled flag on item.
    dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        contextMenu: CONTEXT_MENU_FILE_BROWSER.MAIN,
        items: sContextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][
          CONTEXT_MENU_FILE_BROWSER.MAIN
        ].items.map((item) => {
          let newItem: ContextMenuItem = Object.assign({}, item);
          newItem.enabled = ['mnu-main-upload', 'mnu-main-createfolder'].includes(item.id)
            ? true
            : item.id === 'mnu-main-paste'
              ? sClipboard.length > 0
              : false;
          return newItem;
        }),
      }),
    );

    dispatch(
      ctx_setContextRow({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        row: -1,
      }),
    );
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        visible: false,
      }),
    );
    dispatch(setContextMenuRefresh(Date.now()));
    //
  } // handleContainerDeselection

  //	------------------------------------------------------------
  //
  //	Handles clicks on toolbar buttons and contextual UI elements
  //  tied to the File Browser
  //
  //	------------------------------------------------------------

  function handleToolbarActions(event: React.MouseEvent<HTMLElement>) {
    //
    const id = event.currentTarget.id;

    switch (id) {
      case 'icon-size-large':
      case 'icon-size-medium':
      case 'icon-size-details': {
        const params: GatewaySettingsType = {
          iconSize:
            id === 'icon-size-details'
              ? 'list'
              : id === 'icon-size-large'
                ? 'large'
                : 'medium',
        };
        const newEvent: Event = {
          eventTarget: EventTarget.APP,
          eventType: EventType.SET_PREFERENCES,
          parameters: params,
        };
        dispatch(handleEvent({ event: newEvent }));
        break;
      }

      case 'refresh-page-or-cancel':
        if (sLoadingComponent === true) {
          cancelQuery();
        } else {
          dispatch(refreshPage());
        }
        break;

      case 'approve-rename':
        if (sRenameType === RenameType.NEW) {
          addFolder({
            name: sRenameFilename,
            storageType:
              sPageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
          });
        } else {
          dispatch(
            renameFile({
              oldFilename: sDataList[sRenameIndex].filename,
              newFilename: sRenameFilename,
              storageType:
                sPageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
              setLoadingFlag: setLoadingComponent,
              runAfterwards: refreshPage,
            }),
          );
          setRenameInProgress(true);
        }
        event.stopPropagation();
        break;

      case 'cancel-rename':
        if (sRenameType === RenameType.NEW) {
          removeTemporaryItem({ index: sRenameIndex });
        }
        dispatch(setRenameIndex(-1));
        event.stopPropagation();
        break;

      case 'upload-button':
        fileDialogUpload?.current?.click();
        break;

      case 'create-directory-button':
        if (sRenameIndex === -1) {
          addTemporaryItem();
          dispatch(setClipboard([]));
        }
        break;

      case 'rename-button':
        dispatch(setRenameIndex(sSelectedDivs[0]));
        dispatch(setRenameType(RenameType.EXISTING));
        dispatch(setRenameFilename(sDataList[sSelectedDivs[0]].filename));
        dispatch(setClipboard([]));
        break;

      case 'delete-button':
        dispatch(
          deleteFile({
            filename: sDataList[sSelectedDivs[0]].filename,
            storageType:
              sPageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
            setLoadingFlag: setLoadingComponent,
            runAfterwards: refreshPage,
          }),
        );
        break;

      case 'cut-button':
        const clipboard = sSelectedDivs.map((i) => sDataList[i].filename);
        dispatch(setClipboard(clipboard));
        dispatch(setClipboardPath(sStoragePath.slice()));
        setInClipboardFolder(true);
        dispatch(setRenameIndex(-1));
        break;

      case 'paste-button':
        moveFiles({
          fromPath: sClipboardPath,
          filenames: sClipboard,
          toPath: sStoragePath,
          storageType:
            sPageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
        });
        dispatch(setClipboard([]));
        dispatch(setClipboardPath([]));
        dispatch(setInClipboardFolder(false));
        dispatch(setSelectedDivs([]));
        break;

      case 'download-button':
        downloadFile({
          file: sDataList[sSelectedDivs[0]],
          storageType:
            sPageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
        });
        break;
    }
    //
  } // handleToolbarActions

  //	------------------------------------------------------------
  //
  //	Displays the right-click context menu
  //
  //	------------------------------------------------------------

  function handleRightClickContextMenu(
    event: React.MouseEvent<HTMLDivElement>,
    divElement: HTMLDivElement,
  ) {
    //
    const scrollDiv = document.querySelector<HTMLDivElement>(
      '#data-management-scrollbox',
    );
    const scrollPos = scrollDiv?.scrollTop ?? 0;
    const bounds = divElement.getBoundingClientRect();

    // update the text in the main context menu.
    ctx_buildContextMenu({
      contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
      contextMenu: CONTEXT_MENU_FILE_BROWSER.MAIN /* MAIN menu */,
      parentRowID: -1,
    });

    // check if this position is OK.
    ctx_calculatePosition({
      contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
      contextMenu: CONTEXT_MENU_FILE_BROWSER.MAIN,
      pos: {
        x: event.clientX,
        y: event.clientY,
      },
    });

    // set only the first menu visible.
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        contextMenu: CONTEXT_MENU_FILE_BROWSER.MAIN,
        visible: true,
      }),
    );
    focusDataItem();

    // close user dropdown menu.
    dispatch(setUserDropdownMenuDisplayed(false));
    //
  } // handleRightClickContextMenu

  //	------------------------------------------------------------
  //
  //	Handles the functinoality when an item is double-clicked
  //	i.e navigating into storage paths
  //
  //	------------------------------------------------------------

  function handleDoubleClickNavigation(divID: number) {
    //
    const stateFetch: any = store.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;
    const stateGlobal: GlobalState = stateFetch.global;

    const pageDisplayed: FileBrowserPage = state.pageParams.pageDisplayed;

    const dataItem: DataItemType = state.dataList[divID];
    const didType: string = dataItem?.did_type?.toUpperCase() ?? '';
    clearTimeout(doubleClickTimeout.current);

    const raiseDMSearch = (params: InitiateDMSearchParams) => {
      const newEvent: Event = {
        eventTarget: EventTarget.DATA_MANAGEMENT_TABLE,
        eventType: EventType.INITIATE_DM_SEARCH,
        parameters: params,
      };
      dispatch(
        handleEvent({
          event: newEvent,
        }),
      );
    };

    const addFilenameToFilterCache = () => {
      //
      // add a new item to the filename filter cache.
      let filterCache: string[] = state.pageParams.filenameFilter.slice();
      filterCache.push('');
      dispatch(setFilenameFilter(filterCache));
      //
    };

    switch (didType) {
      case 'DATASET':
        addFilenameToFilterCache();
        raiseDMSearch({
          namespace: dataItem.namespace,
          filename: '',
          dataset: dataItem.filename,
          fileType: 'all',
          pageDisplayed: FileBrowserPage.Dataset,
          focus: true,
        });
        break;

      case 'NAMESPACE':
        addFilenameToFilterCache();
        if (pageDisplayed === FileBrowserPage.Namespaces) {
          raiseDMSearch({
            namespace: dataItem.filename,
            fileType: 'all',
            pageDisplayed: FileBrowserPage.Items,
            focus: true,
          });
        } else if (pageDisplayed === FileBrowserPage.FileBrowser) {
          raiseDMSearch({
            namespace: '',
            fileType: 'all',
            pageDisplayed: FileBrowserPage.Namespaces,
            focus: true,
          });
        }
        break;

      case 'COLLECTION':
        if (pageDisplayed === FileBrowserPage.FileBrowser) {
          addFilenameToFilterCache();
          raiseDMSearch({
            namespace: '',
            filename: '',
            fileType: 'all',
            project: stateGlobal.project,
            pageDisplayed: FileBrowserPage.Collections,
            focus: true,
          });
        } else if (pageDisplayed === FileBrowserPage.Collections) {
          addFilenameToFilterCache();
          raiseDMSearch({
            namespace: dataItem.namespace,
            filename: '',
            collection: { id: dataItem.id ?? -1, name: dataItem.filename },
            fileType: 'all',
            pageDisplayed: FileBrowserPage.Collection,
            focus: true,
          });
        }
        break;

      case 'USER_STORAGE':
        dispatch(setStoragePath([]));
        addFilenameToFilterCache();
        raiseDMSearch({
          namespace: '',
          filename: '',
          preferredUsername: stateGlobal.preferredUsername,
          storagePath: [],
          canfarService: getSelectedCanfar(stateFetch),
          showHiddenFiles: state.pageParams.showHiddenFiles,
          fileType: 'all',
          pageDisplayed: FileBrowserPage.UserStorage,
          storageType: 'user',
          focus: true,
        });
        break;

      case 'PROJECT_STORAGE':
        dispatch(setStoragePath([]));
        addFilenameToFilterCache();
        raiseDMSearch({
          namespace: '',
          filename: '',
          preferredUsername: stateGlobal.preferredUsername,
          project: stateGlobal.project,
          storagePath: [],
          canfarService: getSelectedCanfar(stateFetch),
          showHiddenFiles: state.pageParams.showHiddenFiles,
          fileType: 'all',
          pageDisplayed: FileBrowserPage.ProjectStorage,
          storageType: 'project',
          focus: true,
        });
        break;

      case 'DIRECTORY':
        if (
          pageDisplayed === FileBrowserPage.UserStorage ||
          pageDisplayed === FileBrowserPage.ProjectStorage
        ) {
          //
          const storagePath: string[] = [
            ...Object.assign([], state.pageParams.storagePath),
            ...[dataItem.filename],
          ];

          addFilenameToFilterCache();
          raiseDMSearch({
            namespace: '',
            filename: '',
            preferredUsername: stateGlobal.preferredUsername,
            storagePath,
            project: stateGlobal.project,
            canfarService: getSelectedCanfar(stateFetch),
            showHiddenFiles: state.pageParams.showHiddenFiles,
            fileType: 'all',
            pageDisplayed: pageDisplayed,
            storageType:
              pageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
            focus: true,
          });
          //
        }
        break;
    }
    //
  } // handleDoubleClickNavigation

  //  ------------------------------------------------------------
  //
  //  handle all key presses on this component.
  //
  //  ------------------------------------------------------------

  function onKeyDownHandler(event: React.KeyboardEvent<HTMLElement>) {
    //
    const stateFetch: any = store.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;

    const pageDisplayed: FileBrowserPage = state.pageParams.pageDisplayed;

    // first, see if the context menu handler wants to process this key press.
    const handled: boolean = ctx_keyDownHandler({ key: event.key });

    // the event was handled by the context-menu handler. stop the propagation.
    if (handled === true) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    // listen for a ctrl-X, and cut currently selected items.
    if (
      event.keyCode === 88 &&
      event.ctrlKey === true &&
      (pageDisplayed === FileBrowserPage.UserStorage ||
        pageDisplayed === FileBrowserPage.ProjectStorage) &&
      state.selectedDivs.length > 0
    ) {
      //
      // put the currently selected items onto the clipboard.
      var clipboard: string[] = [];
      for (var i: number = 0; i < state.selectedDivs.length; i++) {
        clipboard.push(state.dataList[state.selectedDivs[i]].filename);
      }
      dispatch(setClipboard(clipboard));
      dispatch(setClipboardPath(state.pageParams.storagePath.slice()));
      dispatch(setInClipboardFolder(true));

      // disable renaming
      dispatch(setRenameIndex(-1));
      //
    }

    // if we've clicked to paste the current line.
    if (
      event.keyCode === 86 &&
      event.ctrlKey === true &&
      (pageDisplayed === FileBrowserPage.UserStorage ||
        pageDisplayed === FileBrowserPage.ProjectStorage) &&
      state.clipboard.length > 0
    ) {
      moveFiles({
        fromPath: state.clipboardPath,
        filenames: state.clipboard,
        toPath: state.pageParams.storagePath,
        storageType: pageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
      });
      dispatch(setClipboard([]));
      dispatch(setClipboardPath([]));
      dispatch(setInClipboardFolder(false));
      dispatch(setSelectedDivs([]));
    }

    // if we've clicked to rename an item in the user storage.
    if (
      event.keyCode === 113 &&
      (pageDisplayed === FileBrowserPage.UserStorage ||
        pageDisplayed === FileBrowserPage.ProjectStorage) &&
      state.selectedDivs.length === 1
    ) {
      dispatch(setRenameIndex(state.selectedDivs[0]));
      dispatch(setRenameType(RenameType.EXISTING));
      dispatch(setRenameFilename(state.dataList[state.selectedDivs[0]].filename));

      // disable cutting
      dispatch(setClipboard([]));
    }

    // if we've clicked to delete the current item.
    if (
      event.keyCode === 46 &&
      (pageDisplayed === FileBrowserPage.UserStorage ||
        pageDisplayed === FileBrowserPage.ProjectStorage) &&
      state.selectedDivs.length === 1
    ) {
      dispatch(
        deleteFile({
          filename: state.dataList[state.selectedDivs[0]].filename,
          storageType: pageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
          setLoadingFlag: setLoadingComponent,
          runAfterwards: refreshPage,
        }),
      );
      dispatch(setSelectedDivs([]));
    }

    const renameFieldActive =
      state.focusElement.divID === state.renameIndex &&
      event.target instanceof HTMLInputElement &&
      event.target.name === 'rename-box';

    // if we're currently renaming a file, and enter is pressed.....
    if (renameFieldActive && event.key === 'Enter') {
      //
      if (state.renameType === RenameType.NEW) {
        addFolder({
          name: state.renameFilename,
          storageType: pageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
        });
      } else {
        dispatch(
          renameFile({
            oldFilename: state.dataList[state.renameIndex].filename,
            newFilename: state.renameFilename,
            storageType:
              pageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
            setLoadingFlag: setLoadingComponent,
            runAfterwards: refreshPage,
          }),
        );
        setRenameInProgress(true);
      }
      return;
      //
    }

    // if we're currently renaming a file, and escape is pressed.....
    if (renameFieldActive && event.key === 'Escape') {
      if (state.renameType === RenameType.NEW) {
        removeTemporaryItem({ index: state.renameIndex });
      }
      dispatch(setRenameIndex(-1));
      return;
    }

    // if we're currently running a query, and escape is pressed.....
    if (sLoadingComponent === true && event.key === 'Escape') {
      cancelQuery();
    }

    // if enter is pressed, then activate a double-click on the focused element.
    if (!renameFieldActive && event.key === 'Enter' && state.dataList.length > 0) {
      event.preventDefault();
      if (state.focusElement.divID > -1) {
        handleDoubleClickNavigation(state.focusElement.divID);
      }
      return;
    }

    // if enter or space is pressed, then activate a double-click on the focused element.
    if (!renameFieldActive && event.key === ' ' && state.dataList.length > 0) {
      event.preventDefault();
      if (state.focusElement.divID > -1) {
        //
        // is this item already selected?
        if (state.selectedDivs.indexOf(state.focusElement.divID) > -1) {
          dispatch(setSelectedDivs([]));
        } else {
          brw_handleItemClick({
            divID: state.focusElement.divID,
            shiftKey: event.shiftKey,
            ctrlKey: event.ctrlKey,
            selectedDivs: state.selectedDivs,
            setSelectedDivs: setSelectedDivs,
            addToSelectedDivs: addToSelectedDivs,
            removeFromSelectedDivs: removeFromSelectedDivs,
            focusElement: state.focusElement.divID,
            setFocusElement: setFocusElement,
          });
        }
        //
      }
      return;
    }

    // if the rename box has focus then go no further.
    if (event.target instanceof HTMLInputElement && event.target.name === 'rename-box') {
      return;
    }

    // count the number of items across each row.
    const itemsPerRow = getItemsPerRow();

    // check if the item browser handler wishes to handle this key down event.
    // this handler moves the focus from one item to another, and handles
    // the arrow keys being pressed in conjunction with the shift key.
    if (
      brw_keyDownHandler({
        event: event,
        dataItems: state.dataList.length,
        itemsPerRow: itemsPerRow,
        selectedDivs: state.selectedDivs,
        setSelectedDivs: setSelectedDivs,
        addToSelectedDivs: addToSelectedDivs,
        removeFromSelectedDivs: removeFromSelectedDivs,
        focusElement: state.focusElement.divID,
        setFocusElement: setFocusElement,
      }) === true
    ) {
      //
      // stop the propagation.
      event.preventDefault();
      event.stopPropagation();

      // ensure the focus hasn't moved.
      focusDataItem();
      //
    }
    //
  } // onKeyDownHandler

  //	------------------------------------------------------------
  //
  //	Enables/disables context menu items based on current selection
  //  state
  //
  //	------------------------------------------------------------

  function updateContextMenuItems(args: {
    itemsSelected: number;
    singleFileSelected: boolean;
  }) {
    //
    dispatch(
      ctx_setContextMenuDefinition({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        contextMenu: CONTEXT_MENU_FILE_BROWSER.MAIN,
        items: sContextMenuDefinition[CONTEXT_SYSTEMS.FILE_BROWSER][
          CONTEXT_MENU_FILE_BROWSER.MAIN
        ].items.map((item) => {
          let newItem: ContextMenuItem = Object.assign({}, item);
          switch (newItem.id) {
            case 'mnu-main-moveto':
              newItem.enabled =
                [
                  FileBrowserPage.Items,
                  FileBrowserPage.Collections,
                  FileBrowserPage.Collection,
                  FileBrowserPage.Dataset,
                  FileBrowserPage.JobDetails,
                ].includes(sPageDisplayed) && args.itemsSelected > 0;
              break;
            case 'mnu-main-deletecollection':
            case 'mnu-main-removeitemfromcollection':
            case 'mnu-main-upload':
            case 'mnu-main-cut':
              newItem.enabled = true && args.itemsSelected > 0;
              break;
            case 'mnu-main-renamefile':
            case 'mnu-main-deletenode':
              newItem.enabled = args.itemsSelected === 1;
              break;
            case 'mnu-main-paste':
              newItem.enabled = sClipboard.length > 0;
              break;
            case 'mnu-main-download':
              newItem.enabled = args.singleFileSelected;
              break;
          }
          return newItem;
        }),
      }),
    );
    dispatch(setContextMenuRefresh(Date.now()));
    //
  } // updateContextMenuItems

  /*
//
// these functions work really nicely, but the save dialog these show only works in
// a few browsers - currently Edge, Chrome, and Opera. I am leaving this code here
// in case this functionality is rolled out to other browsers in the next few months.
//

async function downloadFile(args: { filename: string }) {
  //
  try {
    const fileHandle = await getSaveFilePicker();
    await saveFile({ fileHandle: fileHandle, filename: args.filename }) 
  } catch (error) {
    console.error(error);
  }
  //
} // downloadFile

async function getSaveFilePicker() {
  //
  const opts = {
    suggestedName: 'cjs1.test',
    types: [{
      accept: { "text/plain": [".test"] },
    }],
  };
  
  return await window.showSaveFilePicker(opts);
  //
} // getSaveFilePicker

async function saveFile(args: { fileHandle: FileSystemFileHandle, filename: string }) {
  //
  // build the from-folder name.
  var fromFolder: string = '';
  if (sStoragePath !== undefined)
    for (var i: number = 0; i < sStoragePath.length; i++)
      fromFolder = fromFolder + sStoragePath[i] + '/';

  // get the url.
  var url: string = '';
  if (props.selectedCanfar !== undefined) {
    url = props.selectedCanfar.url;
  }

  const writable = await args.fileHandle.createWritable();
        
  // build the url.
  var urlCommand: string =
    apiPrefix() +
    '/v1/user_storage/download_file?url=' + url + '&from_file=' + fromFolder + args.filename;

  try {
    //
    const apiResult = await fetch(urlCommand, {
      method: 'GET',
      credentials: 'include',
    });
      
    const disposition = apiResult.headers.get('content-disposition');
      
    var blob: any = await apiResult.blob();
      await writable.write(blob);
      await writable.close();
    //  
  } catch (e) {}
  //
} // saveFile

*/

  //	------------------------------------------------------------
  //
  //	Initiate an upload when files are selected on the
  //	file-upload dialog
  //
  //	------------------------------------------------------------
  /*
  const onDownloadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    console.log('onDownloadChange fires');

    if (event.target.files) {
      downloadFile({ folder: event.target.files[0], file: sDataList[sSelectedDivs[0]] });
    }

    if (event.target.files !== null)
      if (event.target.files.length > 0)
        console.log('download files......', event.target.files[0]);
    //
  }; // onDownloadChange*/

  //	------------------------------------------------------------
  //
  //	Initiate an upload when files are selected on the
  //	file-upload dialog
  //
  //	------------------------------------------------------------

  const onUploadChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    console.log('onUploadChange fires');

    if (event.target.files) {
      uploadFile({
        file: event.target.files[0],
        storageType: sPageDisplayed === FileBrowserPage.UserStorage ? 'user' : 'project',
      });
    }

    if (event.target.files !== null)
      if (event.target.files.length > 0)
        console.log('upload files......', event.target.files[0]);
    //
  }; // onUploadChange

  //	------------------------------------------------------------
  //
  //	If the user cancels naming an item that doesn't yet exist
  //	in storage, we need to remove the icon from the list.
  //
  //	------------------------------------------------------------

  function removeTemporaryItem(args: { index: number }) {
    //
    const stateFetch: any = store.getState();
    const state: FileBrowserState = stateFetch.fileBrowser;
    //
    // remove this item from the data list.
    var dataList: DataItemType[] = state.dataList.slice();
    dataList.splice(args.index, 1);

    // update state.
    dispatch(setDataList(dataList));
    //
  } // removeTemporaryItem

  //	------------------------------------------------------------
  //
  //	An asynchronous function that uploads a file to user
  //	storage.
  //
  //	------------------------------------------------------------

  async function uploadFile(args: { file: File; storageType?: 'user' | 'project' }) {
    console.log('uploadFile fires:', args.file);
    dispatch(setLoadingComponent(true));

    const { file, storageType = 'user' } = args;

    const path = sStoragePath?.join('/') ?? '';
    const url = sG_selectedCanfar?.url ?? '';
    const username = sG_preferredUsername ?? '';
    const project_name = sG_project?.name ?? '';

    const basePrefix =
      storageType === 'user' ? `/home/${username}` : `/projects/${project_name}`;
    const toFolder = `${basePrefix}/${path}`;

    const urlCommand =
      `${apiPrefix()}/v1/storage/upload_file` +
      `?url=${encodeURIComponent(url)}` +
      `&to_folder=${encodeURIComponent(toFolder)}`;

    const formData = new FormData();
    formData.append('file_to_upload', file);

    try {
      const apiResult = await fetch(urlCommand, {
        method: 'PUT',
        credentials: 'include',
        body: formData,
      });

      if (apiResult.status === 201) {
        dispatch(setLoadingComponent(false));
        dispatch(refreshPage());
      } else {
        let rawJson = await apiResult.text();
        const parsedJson = JSON.parse(rawJson, (key, value) => {
          return value;
        });
        dispatch(setLoadingComponent(false));
        if (parsedJson.detail.includes('403 Forbidden')) {
          dispatch(refreshPage());
          toast.error('Request forbidden by administrative rules.', {
            position: 'top-right',
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: false,
            progress: undefined,
            theme: sG_darkMode ? 'dark' : 'light',
          });
        }
      }
    } catch (e) {
      console.error('Upload error:', e);
      dispatch(setLoadingComponent(false));
    }
  }

  //	------------------------------------------------------------
  //
  //	H O O K S
  //
  //	------------------------------------------------------------

  //	------------------------------------------------------------
  //
  //	open a file dialog.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    if (sOpenFileDialogTrigger > 0) {
      fileDialogUpload?.current?.click();
    }
    //
  }, [sOpenFileDialogTrigger]);

  //	------------------------------------------------------------
  //
  //	rebuild the context menus when the page changes.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    // update state.
    dispatch(setRenameIndex(-1));
    setRenameInProgress(false);
    //
  }, [sPageDisplayed]);

  //	------------------------------------------------------------
  //
  //	update the selected items when the selected divs change.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    dispatch(setSelectedItems(getSelectedItems({ divID: sSelectedDivs })));
    //
  }, [sSelectedDivs]);

  //	------------------------------------------------------------
  //
  //	set the enabled status on the context menu items
  //	depending on whether or not we have items selected.
  //
  //	------------------------------------------------------------

  useEffect(() => {
    //
    let itemsSelected = sSelectedDivs.length;
    let singleFileSelected: boolean = false;
    if (itemsSelected === 1) {
      if (sDataList[sSelectedDivs[0]]?.did_type?.toUpperCase() === 'FILE') {
        singleFileSelected = true;
      }
    }

    updateContextMenuItems({
      itemsSelected: itemsSelected,
      singleFileSelected: singleFileSelected,
    });
    //
  }, [sSelectedItems]);

  //  ------------------------------------------------------------
  //
  //  reset the focused element when the page is refreshed.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    // hide the menu.
    dispatch(
      ctx_setContextVisible({
        contextSystem: CONTEXT_SYSTEMS.FILE_BROWSER,
        visible: false,
      }),
    );
    //
  }, [sDataList]);

  //  ------------------------------------------------------------
  //
  //  if the focused element in the file browser is changed,
  //  then we need to set the focus here.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    if (sFocusElement.divID > -1) {
      focusDataItem();
    }
    //
  }, [sFocusElement]);

  useEffect(() => {
    //
    if (sLoadingComponent === true) {
      const button: any = document.getElementById('refresh-page-or-cancel');
      if (button !== null) {
        button.focus();
      }
    }
    //
  }, [sLoadingComponent]);

  //  ------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  ------------------------------------------------------------

  return (
    <div
      /*
      //  main window and side buttons, and also bottom controls.
      */
      className="panel-main"
      style={{
        flexDirection: 'column',
        margin: '0px',
        flex: '1 1',
      }}
    >
      <div
        /*
        //  main window and side buttons.
        */
        style={{
          flex: '1 1',
          display: 'flex',
          flexDirection: 'row',
          width: '100%',
          padding: '0px',
        }}
      >
        <div
          /*
          //  Column to contain the main window.
          */
          id="file-browser-container"
          style={{
            flex: '1 1',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: 'none',
            border: 'none',
          }}
          onKeyDown={onKeyDownHandler}
        >
          {/*
           //  The data-items window, with a vertical scrollbar if required.
           // */}
          <Scrollbox
            id="data-management-scrollbox"
            direction="vertical"
            width="100%"
            flex="1 1"
            flexDirection="column"
            padding="10px 10px 10px 10px"
          >
            {/* display the list of data items */}
            <DataItems
              key={
                sDataList.length.toString() +
                sSelectedDivs.length.toString() +
                sRenameIndex.toString() +
                sFocusElement.toString()
              }
              dataList={sDataList}
              errorMessage={sErrorMessage}
              selectedCanfar={sG_selectedCanfar}
              displayScope={
                sPageDisplayed === FileBrowserPage.Collection ||
                sPageDisplayed === FileBrowserPage.JobDetails
              }
              selectedItems={sSelectedDivs}
              clipboard={sInClipboardFolder ? sClipboard : []}
              editingIndex={sRenameIndex}
              renameInProgress={sRenameInProgress}
              focusIndex={sFocusElement.divID}
              onClickHandler={onClickHandler}
            />
          </Scrollbox>
          <div style={{ flex: '0 0 auto', marginLeft: '10px' }}>
            <PleaseWait
              text={t('Please wait') + '.....'}
              visible={sLoadingComponent === true}
            />
          </div>
        </div>

        {/*
         //  Column to contain the command buttons, i.e. cut, copy, paste, etc.
         */}
        <div
          style={{
            flex: '0 0 55px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <div style={{ flex: '0 0 5px' }} />
          <MiniButton
            id="create-directory-button"
            data-selected="F"
            onClick={onClickHandler}
            disabled={
              !(
                sPageDisplayed === FileBrowserPage.UserStorage ||
                sPageDisplayed === FileBrowserPage.ProjectStorage
              ) || sLoadingComponent
            }
            aria-label={t('Create folder')}
            title={t('Create folder')}
          >
            <FolderPlus
              className="text-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              data-muted={
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sLoadingComponent === false
                  ? 'F'
                  : 'T'
              }
              data-mini-button="T"
            />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />
          <MiniButton
            id="rename-button"
            data-selected="F"
            onClick={onClickHandler}
            disabled={
              !(
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sSelectedDivs.length === 1
              ) || sLoadingComponent
            }
            aria-label={t('Rename file')}
            title={t('Rename file')}
          >
            <FilePen
              className="text-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              data-muted={
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sSelectedDivs.length === 1 &&
                sLoadingComponent === false
                  ? 'F'
                  : 'T'
              }
              data-mini-button="T"
            />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />
          <MiniButton
            id="delete-button"
            data-selected="F"
            onClick={onClickHandler}
            disabled={
              !(
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sSelectedDivs.length === 1
              ) || sLoadingComponent
            }
            aria-label={t('Delete file/folder')}
            title={t('Delete file/folder')}
          >
            <XIcon
              className="text-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              data-muted={
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sSelectedDivs.length === 1 &&
                sLoadingComponent === false
                  ? 'F'
                  : 'T'
              }
              data-text-red="T"
            />
          </MiniButton>
          <div style={{ flex: '0 0 15px' }} />
          <MiniButton
            id="copy-button"
            data-selected="F"
            disabled={true}
            aria-label={t('Copy file(s) - not currently implemented')}
            title={t('Copy file(s) - not currently implemented')}
          >
            <Copy
              className="text-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              data-muted="T"
              data-mini-button="T"
            />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />
          <MiniButton
            id="cut-button"
            data-selected="F"
            onClick={onClickHandler}
            disabled={
              !(
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sSelectedDivs.length > 0
              )
            }
            aria-label={t('Cut file(s)')}
            title={t('Cut file(s)')}
          >
            <Scissors
              className="text-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              data-muted={
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sSelectedDivs.length > 0 &&
                sLoadingComponent === false
                  ? 'F'
                  : 'T'
              }
              data-mini-button="T"
            />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />
          <MiniButton
            id="paste-button"
            data-selected="F"
            onClick={onClickHandler}
            disabled={
              !(
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sClipboard.length > 0
              ) || sLoadingComponent
            }
            aria-label={t('Paste file(s)')}
            title={t('Paste file(s)')}
          >
            <ClipboardPaste
              className="text-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              data-muted={
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sClipboard.length > 0 &&
                sLoadingComponent === false
                  ? 'F'
                  : 'T'
              }
              data-mini-button="T"
            />
          </MiniButton>
          <div style={{ flex: '0 0 15px' }} />
          <MiniButton
            id="upload-button"
            data-selected="F"
            onClick={onClickHandler}
            disabled={
              !(
                sPageDisplayed === FileBrowserPage.UserStorage ||
                sPageDisplayed === FileBrowserPage.ProjectStorage
              ) || sLoadingComponent
            }
            aria-label={t('Upload file')}
            title={t('Upload file')}
          >
            <FileUp
              className="text-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              data-muted={
                sPageDisplayed === FileBrowserPage.UserStorage ||
                (sPageDisplayed === FileBrowserPage.ProjectStorage &&
                  sLoadingComponent === false)
                  ? 'F'
                  : 'T'
              }
              data-mini-button="T"
            />
          </MiniButton>
          <div style={{ flex: '0 0 5px' }} />
          <MiniButton
            id="download-button"
            data-selected="F"
            onClick={onClickHandler}
            disabled={
              !(
                (sPageDisplayed === FileBrowserPage.UserStorage ||
                  sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                sSelectedDivs.length === 1 &&
                sDataList[sSelectedDivs[0]].did_type?.toLowerCase() === 'file'
              ) || sLoadingComponent
            }
            aria-label={t('Download file')}
            title={t('Download file')}
          >
            <FileDown
              className="text-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              data-muted={
                !(
                  (sPageDisplayed === FileBrowserPage.UserStorage ||
                    sPageDisplayed === FileBrowserPage.ProjectStorage) &&
                  sSelectedDivs.length === 1 &&
                  sDataList[sSelectedDivs[0]].did_type?.toLowerCase() === 'file'
                ) || sLoadingComponent
                  ? 'T'
                  : 'F'
              }
              data-mini-button="T"
            />
          </MiniButton>
        </div>
      </div>

      <div style={{ flex: '0 0 20px' }} />

      <div className="fb-footer">
        {sDataList.length === 100 &&
          sPageDisplayed !== FileBrowserPage.FileBrowser &&
          sPageDisplayed !== FileBrowserPage.Namespaces && (
            <div className="fb-footer__info text-colour" style={{ order: '0' }}>
              Showing first 100 results
            </div>
          )}
        {sG_narrowScreen === false && <div style={{ flex: '1 1' }} />}

        <div
          className="fr"
          style={{
            alignItems: 'center',
            gap: '10px',
            flex: '0 0 auto',
          }}
        >
          <label
            id="file-browser-file-type-label"
            className="text-colour"
            style={{
              flex: '0 0 auto',
              order: '0',
            }}
          >
            File type:
          </label>

          <select
            id="file-type-combo"
            aria-labelledby="file-browser-file-type-label"
            className="listbox"
            multiple={false}
            onChange={onChangeSelectHandler}
            disabled={
              sPageDisplayed === FileBrowserPage.FileBrowser ||
              sPageDisplayed === FileBrowserPage.Namespaces ||
              sPageDisplayed === FileBrowserPage.Collections ||
              sLoadingComponent
            }
            data-disabled={
              sPageDisplayed === FileBrowserPage.FileBrowser ||
              sPageDisplayed === FileBrowserPage.Namespaces ||
              sPageDisplayed === FileBrowserPage.Collections ||
              sLoadingComponent
                ? 'T'
                : 'F'
            }
            value={
              sPageDisplayed === FileBrowserPage.FileBrowser ||
              sPageDisplayed === FileBrowserPage.Namespaces ||
              sPageDisplayed === FileBrowserPage.Collections
                ? 'all'
                : sFileTypeFilter
            }
            style={{
              flex: '1 1',
            }}
          >
            <option value="all">{t('All types')}</option>
            <option value="file">{t('Files')}</option>
            <option value="dataset">{t('Datasets')}</option>
            <option value="container">{t('Containers')}</option>
            <option value="directory">{t('Directories')}</option>
          </select>
        </div>

        <div
          className="fr"
          style={{
            ...{
              alignItems: 'center',
              gap: '10px',
              width: 'auto',
            },
            ...(sG_narrowScreen ? { width: '100%' } : {}),
          }}
        >
          <label
            id="file-browser-file-filter-label"
            className="text-colour"
            style={{
              flex: '0 0 auto',
              order: '0',
            }}
          >
            Filter filenames:
          </label>

          {/*<div className="fb-footer__filter">*/}
          <input
            className="input-field"
            type="text"
            id="file-browser-filename-filter"
            aria-labelledby="file-browser-file-filter-label"
            name="filename"
            placeholder={t('Filename')}
            onBlur={onBlurEvent}
            onChange={onChangeEvent}
            value={filenameFilter()}
            maxLength={255}
            disabled={sLoadingComponent}
            data-disabled={sLoadingComponent ? 'T' : 'F'}
          />
          {/*</div>*/}
          {sG_narrowScreen === true && <div style={{ flex: '1 1' }} />}
          {sG_narrowScreen === false && (
            <MiniButton
              id="icon-size-details"
              className="text-colour"
              data-selected={sIconSize === 'list' ? 'T' : 'F'}
              data-mini-button="T"
              data-muted={sLoadingComponent ? 'T' : 'F'}
              onClick={onClickHandler}
              aria-label={t('Detailed list')}
              title={t('Detailed list')}
              disabled={sLoadingComponent}
            >
              <List width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
          )}

          {sG_narrowScreen === false && (
            <MiniButton
              id="icon-size-medium"
              className="text-colour"
              data-selected={
                sIconSize === 'medium' || sIconSize === undefined ? 'T' : 'F'
              }
              data-mini-button="T"
              data-muted={sLoadingComponent ? 'T' : 'F'}
              onClick={onClickHandler}
              aria-label={t('Small icons')}
              title={t('Small icons')}
              disabled={sLoadingComponent}
            >
              <FileIcon width={20} height={20} strokeWidth={1.5} />
            </MiniButton>
          )}

          {sG_narrowScreen === false && (
            <MiniButton
              id="icon-size-large"
              className="text-colour"
              data-selected={sIconSize === 'large' ? 'T' : 'F'}
              data-mini-button="T"
              data-muted={sLoadingComponent ? 'T' : 'F'}
              onClick={onClickHandler}
              aria-label={t('Large icons')}
              title={t('Large icons')}
              disabled={sLoadingComponent}
            >
              <FileIcon width={30} height={30} strokeWidth={1.5} />
            </MiniButton>
          )}

          <MiniButton
            id="refresh-page-or-cancel"
            className="fb-footer__icon"
            onClick={onClickHandler}
            aria-label={sLoadingComponent ? t('Cancel') : t('Refresh')}
            title={sLoadingComponent ? t('Cancel') : t('Refresh')}
          >
            {sLoadingComponent && (
              <XIcon
                className="text-colour"
                width={30}
                height={30}
                strokeWidth={2}
                data-text-red="T"
              />
            )}
            {sLoadingComponent === false && (
              <RefreshCw
                className="text-colour"
                width={30}
                height={30}
                strokeWidth={2}
                data-text-green="T"
              />
            )}
          </MiniButton>
        </div>

        <input
          type="file"
          id="file"
          ref={fileDialogUpload}
          onChange={onUploadChange}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
} // DataManagementTable

//	--------------------------------------------------------------------------
//
//	S U B   C O M P O N E N T S
//
//	--------------------------------------------------------------------------

//	------------------------------------------------------------
//
//	Display a small sized icon for each item, with a single
//	row of details.
//
//	------------------------------------------------------------

function DataItemDetails(props: {
  id: string;
  item: DataItemType;
  itemSelected: boolean;
  itemFocused: boolean;
  itemCut: boolean;
  onClickHandler: any;
  displayScope: boolean;
  enabled: boolean;
  editing: boolean | undefined;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  const renameReference = React.createRef<HTMLInputElement>();

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sRenameFilename: string = useAppSelector(
    (state) => state.fileBrowser.renameFilename,
  );

  //  ------------------------------------------------------------
  //
  //  handler for changes to the input boxes.
  //
  //  ------------------------------------------------------------

  const inputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    const inputBox: HTMLInputElement = event.target;

    // update the state for any input boxes that have been changed.
    if (inputBox.name === 'rename-box') {
      dispatch(setRenameFilename(inputBox.value));
    }
  }; // inputHandler

  //  ------------------------------------------------------------
  //
  //  handle onClick events for the input boxes, so that we can
  //  stop propagation.
  //
  //  ------------------------------------------------------------

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    //
    event.stopPropagation();
    //
  }; // onClick

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  set focus to rename field.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    renameReference.current?.focus();
    //
  });

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <div
      className="data-management-item"
      style={{
        width: '100%',
        margin: '2px 2px 2px 2px',
        float: 'left',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
      id={props.id}
      data-selected={props.itemSelected ? 'T' : 'F'}
      data-cut={props.itemCut === true ? 'T' : 'F'}
      onClick={props.enabled ? props.onClickHandler : undefined}
      onContextMenu={props.enabled ? props.onClickHandler : undefined}
      tabIndex={
        props.enabled && props.editing === false && props.itemFocused === true ? 0 : -1
      }
      role="option"
      aria-selected={props.itemSelected ? 'true' : 'false'}
    >
      <div
        style={{
          flex: '0 0 42px',
          display: 'flex',
          flexDirection: 'row',
          margin: '0px 0px 0px 0px',
          alignItems: 'left',
        }}
      >
        <div style={{ flex: '0 0 10px' }} />
        <Icon item={props.item} size={40} enabled={props.enabled} />
        {(props.editing === true || props.editing === undefined) && (
          <div style={{ flex: '1 1', height: '42px', padding: '0px' }}>
            <input
              ref={renameReference}
              aria-label="Enter new filename"
              className="input-field"
              type="text"
              name="rename-box"
              placeholder="Enter new filename"
              onChange={inputHandler}
              onClick={onClick}
              maxLength={50}
              value={sRenameFilename}
              data-highlighted={props.itemSelected === true ? 'T' : 'F'}
              data-disabled={props.editing === undefined ? 'T' : 'F'}
              disabled={props.editing === undefined}
            />
          </div>
        )}
        {(props.editing === true || props.editing === undefined) && (
          <button
            id="approve-rename"
            aria-label="Confirm rename"
            type="button"
            style={{
              position: 'absolute',
              top: '-10px',
              right: '315px',
              width: '30px',
              height: '30px',
              padding: '0px',
              border: 'none',
              background: 'transparent',
            }}
            onClick={props.editing === undefined ? undefined : props.onClickHandler}
          >
            <Check
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={2}
              data-muted={props.editing === undefined}
              data-text-green="T"
              style={{
                borderRadius: '5px',
              }}
            />
          </button>
        )}
        {(props.editing === true || props.editing === undefined) && (
          <button
            id="cancel-rename"
            aria-label="Cancel rename"
            type="button"
            style={{
              position: 'absolute',
              top: '-10px',
              right: '282px',
              width: '30px',
              height: '30px',
              padding: '0px',
              border: 'none',
              background: 'transparent',
            }}
            onClick={props.editing === undefined ? undefined : props.onClickHandler}
          >
            <XIcon
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={2}
              data-muted={props.editing === undefined}
              data-text-red="T"
              style={{
                borderRadius: '5px',
              }}
            />
          </button>
        )}
        {props.editing === undefined && (
          <div
            style={{
              position: 'absolute',
              top: '18px',
              right: '275px',
              width: 'auto',
              height: '30px',
            }}
          >
            {t('Renaming') + '.....'}
          </div>
        )}
        {props.itemCut === true && (
          <div
            style={{
              position: 'absolute',
              top: '-10px',
              right: '12px',
              width: '30px',
              height: '30px',
            }}
          >
            <Scissors
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              style={{
                borderRadius: '5px',
              }}
            />
          </div>
        )}
        <div style={{ flex: '0 0 10px' }} />
        {props.editing === false && (
          <div
            className="text-colour"
            data-selected={props.itemSelected ? 'T' : 'F'}
            style={{
              whiteSpace: 'nowrap',
              flex: '1 1',
              alignItems: 'center',
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'row',
            }}
          >
            {(props.displayScope === true ? props.item.namespace + ':' : '') +
              props.item.filename}
          </div>
        )}
        <div style={{ flex: '0 0 10px' }} />
        <div
          className="text-colour"
          data-selected={props.itemSelected ? 'T' : 'F'}
          style={{
            whiteSpace: 'nowrap',
            flex: '0 0 120px',
            alignItems: 'center',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {props.item.did_type?.toUpperCase() ?? ''}
        </div>
        <div style={{ flex: '0 0 10px' }} />
        <div
          className="text-colour"
          data-selected={props.itemSelected ? 'T' : 'F'}
          style={{
            whiteSpace: 'nowrap',
            flex: '0 0 120px',
            alignItems: 'center',
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'row',
          }}
        >
          {props.item.bytes !== undefined
            ? displayFileSize({ bytes: props.item.bytes ?? 0 })
            : '/'}
        </div>
      </div>
    </div>
  );
  //
} // DataItemDetails

//  ------------------------------------------------------------
//
//  Display a medium sized icon for each item.
//
//  ------------------------------------------------------------

function DataItemMedium(props: {
  id: string;
  item: DataItemType;
  itemSelected: boolean;
  itemFocused: boolean;
  itemCut: boolean;
  onClickHandler: any;
  displayScope: boolean;
  enabled: boolean;
  editing: boolean | undefined;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  const renameReference = React.createRef<HTMLInputElement>();

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sRenameFilename: string = useAppSelector(
    (state) => state.fileBrowser.renameFilename,
  );

  //  ------------------------------------------------------------
  //
  //  handler for changes to the input boxes.
  //
  //  ------------------------------------------------------------

  const inputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    // update the state for any input boxes that have been changed.
    if (inputBox.name === 'rename-box') {
      dispatch(setRenameFilename(inputBox.value));
    }
    //
  }; // inputHandler

  //  ------------------------------------------------------------
  //
  //  handle onClick events for the input boxes, so that we can
  //  stop propagation.
  //
  //  ------------------------------------------------------------

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    //
    event.stopPropagation();
    //
  }; // onClick

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  set focus on rename field.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    renameReference.current?.focus();
    //
  });

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <div
      className="data-management-item"
      style={{
        width: '375px',
        margin: '2px 2px 2px 2px',
        float: 'left',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
      }}
      id={props.id}
      data-selected={props.itemSelected === true ? 'T' : 'F'}
      data-cut={props.itemCut === true ? 'T' : 'F'}
      onClick={props.enabled ? props.onClickHandler : undefined}
      onContextMenu={props.enabled ? props.onClickHandler : undefined}
      tabIndex={
        props.enabled && props.editing === false && props.itemFocused === true ? 0 : -1
      }
      role="option"
      aria-selected={props.itemSelected ? 'true' : 'false'}
    >
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'row',
          margin: '0px 0px 0px 0px',
          alignItems: 'left',
        }}
      >
        <div style={{ flex: '0 0 10px' }} />
        <Icon item={props.item} size={40} enabled={props.enabled} />
        {(props.editing === true || props.editing === undefined) && (
          <div style={{ flex: '0 0 210px', height: '42px', padding: '0px' }}>
            <input
              ref={renameReference}
              aria-label="Enter new filename"
              className="input-field"
              type="text"
              name="rename-box"
              placeholder="Enter new filename"
              onChange={inputHandler}
              onClick={onClick}
              maxLength={50}
              value={sRenameFilename}
              data-highlighted={props.itemSelected === true ? 'T' : 'F'}
              data-disabled={props.editing === undefined ? 'T' : 'F'}
              disabled={props.editing === undefined}
            />
          </div>
        )}
        {(props.editing === true || props.editing === undefined) && (
          <button
            id="approve-rename"
            aria-label="Confirm rename"
            type="button"
            style={{
              position: 'absolute',
              top: '-10px',
              right: '45px',
              width: '30px',
              height: '30px',
              padding: '0px',
              border: 'none',
              background: 'transparent',
            }}
            onClick={props.editing === undefined ? undefined : props.onClickHandler}
          >
            <Check
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={2}
              data-muted={props.editing === undefined}
              data-text-green="T"
              style={{
                borderRadius: '5px',
              }}
            />
          </button>
        )}
        {(props.editing === true || props.editing === undefined) && (
          <button
            id="cancel-rename"
            aria-label="Cancel rename"
            type="button"
            style={{
              position: 'absolute',
              top: '-10px',
              right: '12px',
              width: '30px',
              height: '30px',
              padding: '0px',
              border: 'none',
              background: 'transparent',
            }}
            onClick={props.editing === undefined ? undefined : props.onClickHandler}
          >
            <XIcon
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={2}
              data-muted={props.editing === undefined}
              data-text-red="T"
              style={{
                borderRadius: '5px',
              }}
            />
          </button>
        )}
        {props.editing === undefined && (
          <div
            style={{
              position: 'absolute',
              top: '18px',
              right: '5px',
              width: 'auto',
              height: '30px',
            }}
          >
            {t('Renaming') + '.....'}
          </div>
        )}
        {props.itemCut === true && (
          <div
            style={{
              position: 'absolute',
              top: '-10px',
              right: '12px',
              width: '30px',
              height: '30px',
            }}
          >
            <Scissors
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              style={{
                borderRadius: '5px',
              }}
            />
          </div>
        )}
        {props.editing === false && (
          <div
            className="text-colour"
            data-selected={props.itemSelected ? 'T' : 'F'}
            style={{
              flex: '1 1',
              display: 'flex',
              flexDirection: 'column',
              margin: '0px 5px 0px 5px',
              height: '42px',
            }}
          >
            <div style={{ flex: '1 1' }} />
            <div
              style={{
                overflowWrap: 'anywhere',
                flex: '0 0 auto',
                alignItems: 'center',
                textAlign: 'left',
              }}
            >
              {(props.displayScope === true ? props.item.namespace + ':' : '') +
                props.item.filename}
            </div>
            <div style={{ flex: '1 1' }} />
          </div>
        )}
      </div>
    </div>
  );
  //
} // DataItemMedium

//	------------------------------------------------------------
//
//	Display a large sized icon for each item.
//
//	------------------------------------------------------------

function DataItemLarge(props: {
  id: string;
  item: DataItemType;
  itemSelected: boolean;
  itemFocused: boolean;
  itemCut: boolean;
  onClickHandler: any;
  displayScope: boolean;
  enabled: boolean;
  editing: boolean | undefined;
}) {
  //
  //  --------------------------------------------------------------------------
  //
  //  D E C L A R A T I O N S
  //
  //  --------------------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  const renameReference = React.createRef<HTMLInputElement>();

  //  --------------------------------------------------------------------------
  //
  //  R E D U X   S T A T E   H A N D L I N G
  //
  //  --------------------------------------------------------------------------

  const dispatch = useAppDispatch();

  const sRenameFilename: string = useAppSelector(
    (state) => state.fileBrowser.renameFilename,
  );

  //  ------------------------------------------------------------
  //
  //  handler for changes to the input boxes.
  //
  //  ------------------------------------------------------------

  const inputHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
    //
    const inputBox: HTMLInputElement = event.target;

    // update the state for any input boxes that have been changed.
    if (inputBox.name === 'rename-box') {
      dispatch(setRenameFilename(inputBox.value));
    }
    //
  }; // inputHandler

  //  ------------------------------------------------------------
  //
  //  handle onClick events for the input boxes, so that we can
  //  stop propagation.
  //
  //  ------------------------------------------------------------

  const onClick = (event: React.MouseEvent<HTMLInputElement>) => {
    //
    event.stopPropagation();
    //
  }; // onClick

  //  --------------------------------------------------------------------------
  //
  //  H O O K S
  //
  //  --------------------------------------------------------------------------

  //  ------------------------------------------------------------
  //
  //  set focus on rename field.
  //
  //  ------------------------------------------------------------

  useEffect(() => {
    //
    renameReference.current?.focus();
    //
  });

  //  --------------------------------------------------------------------------
  //
  //  C O M P O N E N T   C O D E
  //
  //  --------------------------------------------------------------------------

  return (
    <div
      className="data-management-item"
      style={{
        width: '245px',
        height: '160px',
        margin: '2px 2px 2px 2px',
        float: 'left',
        display: 'flex',
        flexDirection: 'column',
      }}
      id={props.id}
      data-selected={props.itemSelected === true ? 'T' : 'F'}
      data-cut={props.itemCut === true ? 'T' : 'F'}
      onClick={props.enabled ? props.onClickHandler : undefined}
      onContextMenu={props.enabled ? props.onClickHandler : undefined}
      tabIndex={
        props.enabled && props.editing === false && props.itemFocused === true ? 0 : -1
      }
      role="option"
      aria-selected={props.itemSelected ? 'true' : 'false'}
    >
      <div
        style={{
          flex: '0 0 auto',
          display: 'flex',
          flexDirection: 'column',
          margin: '0px 0px 0px 0px',
          alignItems: 'center',
          position: 'relative',
        }}
      >
        <div style={{ flex: '0 0 10px' }} />
        <Icon item={props.item} size={90} enabled={props.enabled} />
        {(props.editing === true || props.editing === undefined) && (
          <div style={{ flex: '0 0 42px', width: '220px', padding: '0px' }}>
            <input
              ref={renameReference}
              aria-label="Enter new filename"
              className="input-field"
              type="text"
              name="rename-box"
              placeholder="Enter new filename"
              onChange={inputHandler}
              onClick={onClick}
              maxLength={50}
              value={sRenameFilename}
              data-highlighted={props.itemSelected === true ? 'T' : 'F'}
              data-disabled={props.editing === undefined ? 'T' : 'F'}
              disabled={props.editing === undefined}
            />
          </div>
        )}
        {(props.editing === true || props.editing === undefined) && (
          <button
            id="approve-rename"
            aria-label="Confirm rename"
            type="button"
            style={{
              position: 'absolute',
              top: '66px',
              right: '45px',
              width: '30px',
              height: '30px',
              padding: '0px',
              border: 'none',
              background: 'transparent',
            }}
            onClick={props.editing === undefined ? undefined : props.onClickHandler}
          >
            <Check
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={2}
              data-muted={props.editing === undefined}
              data-text-green="T"
              style={{
                borderRadius: '5px',
              }}
            />
          </button>
        )}
        {(props.editing === true || props.editing === undefined) && (
          <button
            id="cancel-rename"
            aria-label="Cancel rename"
            type="button"
            style={{
              position: 'absolute',
              top: '66px',
              right: '12px',
              width: '30px',
              height: '30px',
              padding: '0px',
              border: 'none',
              background: 'transparent',
            }}
            onClick={props.editing === undefined ? undefined : props.onClickHandler}
          >
            <XIcon
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={2}
              data-muted={props.editing === undefined}
              data-text-red="T"
              style={{
                borderRadius: '5px',
              }}
            />
          </button>
        )}
        {props.editing === undefined && (
          <div
            style={{
              position: 'absolute',
              top: '4px',
              right: '140px',
              width: 'auto',
              height: '30px',
            }}
          >
            {t('Renaming') + '.....'}
          </div>
        )}
        {props.itemCut === true && (
          <div
            style={{
              position: 'absolute',
              top: '8px',
              right: '8px',
              width: '50px',
              height: '50px',
            }}
          >
            <Scissors
              className="text-colour background-colour"
              width={30}
              height={30}
              strokeWidth={1.5}
              style={{
                borderRadius: '5px',
              }}
            />
          </div>
        )}
        {props.editing === false && (
          <div
            className="text-colour"
            data-selected={props.itemSelected ? 'T' : 'F'}
            style={{
              overflowWrap: 'anywhere',
              flex: '0 0 42px',
              display: 'flex',
              alignItems: 'center',
              margin: '0px 5px 0px 5px',
              textAlign: 'center',
              boxSizing: 'border-box',
            }}
          >
            {(props.displayScope === true ? props.item.namespace + ':' : '') +
              props.item.filename}
          </div>
        )}
        <div style={{ flex: '0 0 10px' }} />
      </div>
    </div>
  );
  //
} // DataItemLarge

//	------------------------------------------------------------
//
//	Display an icon and name for each data item.
//
//	------------------------------------------------------------

function DataItems(props: {
  dataList: DataItemType[];
  errorMessage: string;
  selectedCanfar: CanfarServiceType | undefined;
  displayScope: boolean;
  selectedItems: number[];
  clipboard: string[];
  editingIndex: number;
  renameInProgress: boolean;
  focusIndex: number;
  onClickHandler: any;
}) {
  //
  //	--------------------------------------------------------------------------
  //
  //	R E D U X   S T A T E   H A N D L I N G
  //
  //	--------------------------------------------------------------------------

  const sLoadingComponent: boolean = useAppSelector(
    (state) => state.fileBrowser.loadingComponent,
  );

  // gateway settings.
  const sIconSize: string | undefined = useAppSelector(
    (state) => state.settings.iconSize,
  );

  // global.
  const sG_narrowScreen: boolean = useAppSelector((state) => state.global.narrowScreen);

  //	--------------------------------------------------------------------------
  //
  //	C O N T E X T   M E N U   D E C L A R A T I O N S
  //
  //	--------------------------------------------------------------------------

  // context-menu definition.
  const sContextMenuRefresh: number = useAppSelector(
    (state) => state.fileBrowser.contextMenuRefresh,
  );

  //	------------------------------------------------------------
  //
  //	D E C L A R A T I O N
  //
  //	------------------------------------------------------------

  // translation function
  const { t } = useTranslation();

  // monitor this container, and if it changes size update the state.
  const dataWindow = ContainerHook({ container: CONTEXT_CONTAINERS.FILE_BROWSER });

  //	------------------------------------------------------------
  //
  //	retrieves the scroll position of the container that
  //	holds the context menu.
  //
  //	------------------------------------------------------------

  function getContainerScrollPosition() {
    //
    var containerScrollPosition: number = 0.0;

    // get the DIV element with the vertical scroll bars, and find the scroll position.
    const id = 'data-management-scrollbox';
    const scrollDiv: HTMLDivElement | null = document.querySelector(`#${id}`);
    if (scrollDiv !== null) containerScrollPosition = scrollDiv.scrollTop;

    // return something.
    return containerScrollPosition;
    //
  } // getContainerScrollPosition

  //	------------------------------------------------------------
  //
  //	checks if this item is enabled or disabled.
  //
  //	------------------------------------------------------------

  function itemEnabled(args: { item: DataItemType }) {
    var enabled: boolean = true;

    // ensure a CANFAR storage location has been selected.
    if (args.item.enabled === DataItemEnabledCondition.SELECTED_CANFAR) {
      enabled = props.selectedCanfar !== undefined;
    }

    // return something.
    return enabled;
  } // itemEnabled

  const hasNoItems = Object.keys(props.dataList).length === 0;

  //	------------------------------------------------------------
  //
  //	C O M P O N E N T   C O D E
  //
  //	------------------------------------------------------------

  return (
    <div
      ref={dataWindow}
      style={{
        flex: '1 1',
        width: '100%',
        textAlign: 'left',
        position: 'relative',
      }}
      id="file-browser-container"
      onClick={props.onClickHandler}
      onContextMenu={props.onClickHandler}
    >
      {hasNoItems ? (
        <div className="no-data-overlay">
          <span>{props.errorMessage}</span>
        </div>
      ) : sLoadingComponent ? (
        <></>
      ) : (
        props.dataList.map((item, index) =>
          sIconSize === 'list' && sG_narrowScreen !== true ? (
            <DataItemDetails
              key={index}
              id={'itmData_' + index.toString()}
              item={item}
              itemSelected={props.selectedItems.indexOf(index) > -1}
              itemFocused={props.focusIndex === index}
              itemCut={props.clipboard.indexOf(item.filename) > -1}
              onClickHandler={props.onClickHandler}
              displayScope={props.displayScope}
              enabled={itemEnabled({ item: item })}
              editing={
                props.editingIndex === index
                  ? props.renameInProgress === true
                    ? undefined
                    : true
                  : false
              }
            />
          ) : (sIconSize === 'medium' || sIconSize === undefined) &&
            sG_narrowScreen !== true ? (
            <DataItemMedium
              key={index}
              id={'itmData_' + index.toString()}
              item={item}
              itemSelected={props.selectedItems.indexOf(index) > -1}
              itemFocused={props.focusIndex === index}
              itemCut={props.clipboard.indexOf(item.filename) > -1}
              onClickHandler={props.onClickHandler}
              displayScope={props.displayScope}
              enabled={itemEnabled({ item: item })}
              editing={
                props.editingIndex === index
                  ? props.renameInProgress === true
                    ? undefined
                    : true
                  : false
              }
            />
          ) : (
            <DataItemLarge
              key={index}
              id={'itmData_' + index.toString()}
              item={item}
              itemSelected={props.selectedItems.indexOf(index) > -1}
              itemFocused={props.focusIndex === index}
              itemCut={props.clipboard.indexOf(item.filename) > -1}
              onClickHandler={props.onClickHandler}
              displayScope={props.displayScope}
              enabled={itemEnabled({ item: item })}
              editing={
                props.editingIndex === index
                  ? props.renameInProgress === true
                    ? undefined
                    : true
                  : false
              }
            />
          ),
        )
      )}

      {/* add the context menus */}
      <ContextMenu
        key={sContextMenuRefresh.toString()}
        system={CONTEXT_SYSTEMS.FILE_BROWSER}
      />
    </div>
  );
  //
} // DataItems

//	------------------------------------------------------------
//
//	Display icon
//
//	------------------------------------------------------------

function Icon(props: { item: DataItemType; size: number; enabled: boolean }) {
  //
  const emptyIconStyle = {
    textAlign: 'center',
    width: props.size.toString() + 'px',
    height: props.size.toString() + 'px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '7pt',
  } as React.CSSProperties;

  if ((props.item.did_type ?? '') !== '') {
    const did_type: string = (props.item.did_type ?? '').toUpperCase();
    switch (did_type) {
      case 'DATASET':
        return (
          <PackageOpen
            className="text-colour"
            width={props.size}
            height={props.size}
            strokeWidth={1}
            data-muted={props.enabled !== true ? 'T' : 'F'}
          />
        );
      case 'FILE':
        return (
          <FileIcon
            className="text-colour"
            width={props.size}
            height={props.size}
            strokeWidth={1}
            data-muted={props.enabled !== true ? 'T' : 'F'}
          />
        );
      case 'CONTAINER':
        return (
          <ShelvingUnit
            className="text-colour"
            width={props.size}
            height={props.size}
            strokeWidth={1}
            data-muted={props.enabled !== true ? 'T' : 'F'}
          />
        );
      case 'NAMESPACE':
        return (
          <Braces
            className="text-colour"
            width={props.size}
            height={props.size}
            strokeWidth={1}
            data-muted={props.enabled !== true ? 'T' : 'F'}
          />
        );
      case 'COLLECTION':
        return (
          <Blocks
            className="text-colour"
            width={props.size}
            height={props.size}
            strokeWidth={1}
            data-muted={props.enabled !== true ? 'T' : 'F'}
          />
        );
      case 'USER_STORAGE':
        return (
          <FolderOpen
            className="text-colour"
            width={props.size}
            height={props.size}
            strokeWidth={1}
            data-muted={props.enabled !== true ? 'T' : 'F'}
          />
        );
      case 'PROJECT_STORAGE':
        return (
          <FolderOpen
            className="text-colour"
            width={props.size}
            height={props.size}
            strokeWidth={1}
            data-muted={props.enabled !== true ? 'T' : 'F'}
          />
        );
      case 'DIRECTORY':
        return (
          <FolderOpen
            className="text-colour"
            width={props.size}
            height={props.size}
            strokeWidth={1}
            data-muted={props.enabled !== true ? 'T' : 'F'}
          />
        );
      default:
        return (
          <div className="border-colour" style={emptyIconStyle} data-icon-border="T">
            {did_type}
          </div>
        );
    }
  } else {
    return (
      <div className="border-colour" style={emptyIconStyle} data-icon-border="T"></div>
    );
  }
  //
} // Icon
